import React, { useRef } from "react";
import { FaBox, FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";
import { resolveHubImage } from "../../utils/b2bHubHomeMedia";

const BROWSE_GRID_MAX_ROWS = 3;
const BROWSE_GRID_COLS = 8;
const BROWSE_GRID_SKELETON_COUNT = BROWSE_GRID_COLS * BROWSE_GRID_MAX_ROWS;

export function readParam(searchParams, key, fallback = "") {
  return searchParams.get(key) ?? fallback;
}

export function buildSearchParams(current, updates) {
  const next = new URLSearchParams(current);
  Object.entries(updates).forEach(([key, value]) => {
    if (value == null || value === "") next.delete(key);
    else next.set(key, String(value));
  });
  if (!("page" in updates)) next.delete("page");
  return next;
}

function CategoryTabThumb({ imagePath, name, fallbackIcon: FallbackIcon = FaBox }) {
  if (imagePath) {
    return (
      <span className="product-marketplace-tabs__icon product-marketplace-tabs__icon--image">
        <img src={resolveHubImage(imagePath)} alt={name} loading="lazy" className="product-marketplace-tabs__img" />
      </span>
    );
  }
  return (
    <span className="product-marketplace-tabs__icon product-marketplace-tabs__icon--fallback">
      <FallbackIcon aria-hidden />
    </span>
  );
}

function CategoryBrowseCard({ item, onSelect }) {
  return (
    <button type="button" className="product-marketplace-browse__item" onClick={() => onSelect(item.id)}>
      <CategoryTabThumb imagePath={item.imagePath} name={item.name} />
      <span className="product-marketplace-tabs__label">{item.name}</span>
    </button>
  );
}

export function CategoryBrowseGrid({ title, subtitle, items, loading, emptyMessage, onSelect }) {
  return (
    <section className="product-marketplace-browse">
      <div className="product-marketplace-browse__intro">
        <h2>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {loading ? (
        <div className="product-marketplace-browse__grid" style={{ "--browse-max-rows": BROWSE_GRID_MAX_ROWS }}>
          {Array.from({ length: BROWSE_GRID_SKELETON_COUNT }).map((_, idx) => (
            <div
              key={idx}
              className="product-marketplace-browse__item product-marketplace-browse__item--skeleton"
              aria-hidden
            />
          ))}
        </div>
      ) : items.length ? (
        <div className="product-marketplace-browse__grid" style={{ "--browse-max-rows": BROWSE_GRID_MAX_ROWS }}>
          {items.map((item) => (
            <CategoryBrowseCard key={item.id} item={item} onSelect={onSelect} />
          ))}
        </div>
      ) : (
        <p className="product-marketplace-browse__empty">{emptyMessage}</p>
      )}
    </section>
  );
}

function CategoryTabsScroller({ children }) {
  const trackRef = useRef(null);

  const scroll = (direction) => {
    trackRef.current?.scrollBy({ left: direction * 280, behavior: "smooth" });
  };

  return (
    <div className="product-marketplace-tabs-scroll">
      <button
        type="button"
        className="product-marketplace-tabs-scroll__arrow product-marketplace-tabs-scroll__arrow--left"
        onClick={() => scroll(-1)}
        aria-label="Scroll left"
      >
        <FaChevronLeft aria-hidden />
      </button>
      <div className="product-marketplace-tabs-scroll__track" ref={trackRef}>
        {children}
      </div>
      <button
        type="button"
        className="product-marketplace-tabs-scroll__arrow product-marketplace-tabs-scroll__arrow--right"
        onClick={() => scroll(1)}
        aria-label="Scroll right"
      >
        <FaChevronRight aria-hidden />
      </button>
    </div>
  );
}

export function CategoryTabsRow({
  ariaLabel,
  items,
  activeId,
  onSelect,
  allLabel = "All Categories",
  showAll = true,
  fallbackIcon: FallbackIcon = FaBox,
}) {
  return (
    <CategoryTabsScroller>
      <div className="product-marketplace-tabs" role="tablist" aria-label={ariaLabel}>
        {showAll ? (
          <button
            type="button"
            className={`product-marketplace-tabs__item${!activeId ? " product-marketplace-tabs__item--active" : ""}`}
            onClick={() => onSelect("")}
          >
            <span className="product-marketplace-tabs__icon product-marketplace-tabs__icon--fallback">
              <FallbackIcon aria-hidden />
            </span>
            <span className="product-marketplace-tabs__label">{allLabel}</span>
          </button>
        ) : null}
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`product-marketplace-tabs__item${String(activeId) === String(item.id) ? " product-marketplace-tabs__item--active" : ""}`}
            onClick={() => onSelect(item.id)}
          >
            <CategoryTabThumb imagePath={item.imagePath} name={item.name} fallbackIcon={FallbackIcon} />
            <span className="product-marketplace-tabs__label">{item.name}</span>
          </button>
        ))}
      </div>
    </CategoryTabsScroller>
  );
}

export function FilterSelect({ id, label, value, disabled, options, placeholder, onChange, onClear }) {
  const selectedLabel = options.find((o) => String(o.value) === String(value))?.label;
  return (
    <div className="product-marketplace-filters__field">
      <label htmlFor={id}>{label}</label>
      <div className={`product-marketplace-filters__select-wrap${value ? " has-value" : ""}`}>
        <select id={id} value={value} disabled={disabled} onChange={onChange}>
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {value ? (
          <button
            type="button"
            className="product-marketplace-filters__clear"
            aria-label={`Clear ${label}`}
            onClick={onClear}
          >
            <FaTimes aria-hidden />
          </button>
        ) : null}
        {value && selectedLabel ? (
          <span className="product-marketplace-filters__select-value">{selectedLabel}</span>
        ) : null}
      </div>
    </div>
  );
}
